import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react'
import {
  getMe,
  logout as performLogout,
  redirectToLogin,
} from '@/lib/auth-client'

type AuthError = {
  type: string
  message: string
}

type AuthContextValue = {
  user: any
  isAuthenticated: boolean
  isLoadingAuth: boolean
  isLoadingPublicSettings: boolean
  authError: AuthError | null
  appPublicSettings: {
    id: string
    public_settings: Record<string, unknown>
  } | null
  logout: (shouldRedirect?: boolean) => void
  navigateToLogin: () => void
  checkAppState: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoadingAuth, setIsLoadingAuth] = useState(true)
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true)
  const [authError, setAuthError] = useState<AuthError | null>(null)
  const [appPublicSettings, setAppPublicSettings] = useState<{
    id: string
    public_settings: Record<string, unknown>
  } | null>(null) // Contains only { id, public_settings }

  const checkUserAuth = useCallback(async () => {
    try {
      // Now check if the user is authenticated
      setIsLoadingAuth(true)
      const currentUser = await getMe()
      setUser(currentUser)
      setIsAuthenticated(true)
      setIsLoadingAuth(false)
    } catch (error) {
      const err = error as { status?: number }
      console.error('User auth check failed:', error)
      setIsLoadingAuth(false)
      setIsAuthenticated(false)

      // If user auth fails, it might be an expired token
      if (err.status === 401 || err.status === 403) {
        setAuthError({
          type: 'auth_required',
          message: 'Authentication required',
        })
      }
    }
  }, [])

  const checkAppState = useCallback(async () => {
    try {
      setIsLoadingPublicSettings(true)
      setAuthError(null)
      setAppPublicSettings({ id: 'domain', public_settings: {} })
      await checkUserAuth()
      setIsLoadingPublicSettings(false)
    } catch (error) {
      const err = error as { message?: string }
      console.error('Unexpected error:', error)
      setAuthError({
        type: 'unknown',
        message: err.message || 'An unexpected error occurred',
      })
      setIsLoadingPublicSettings(false)
      setIsLoadingAuth(false)
    }
  }, [checkUserAuth])

  useEffect(() => {
    checkAppState()
  }, [checkAppState])

  const logoutUser = (shouldRedirect = true) => {
    setUser(null)
    setIsAuthenticated(false)

    if (shouldRedirect) {
      performLogout(window.location.href)
    } else {
      performLogout()
    }
  }

  const navigateToLogin = () => {
    redirectToLogin(window.location.href)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        isLoadingPublicSettings,
        authError,
        appPublicSettings,
        logout: logoutUser,
        navigateToLogin,
        checkAppState,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
